import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import {
  useVoiceInput,
  VoiceLanguage,
} from "../hooks/useVoiceInput";

const LANGUAGES: { code: VoiceLanguage; label: string; flag: string }[] = [
  { code: "am", label: "አማርኛ", flag: "🇪🇹" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

type Props = {
  onTranscript: (text: string) => void;
  language?: VoiceLanguage;
  className?: string;
};

/**
 * Mic button that records audio and delivers transcribed text via onTranscript.
 * Lets the user pick the spoken language before recording.
 */
export default function VoiceInputButton({
  onTranscript,
  language = "en",
  className,
}: Props) {
  const [selectedLang, setSelectedLang] = useState<VoiceLanguage>(language);

  const {
    isRecording,
    isProcessing,
    transcript,
    error,
    start,
    stop,
    reset,
  } = useVoiceInput(selectedLang);

  useEffect(() => {
    if (!transcript) return;
    onTranscript(transcript);
    reset();
  }, [transcript, onTranscript, reset]);

  const handleClick = () => {
    if (isProcessing) return;
    if (isRecording) {
      stop();
    } else {
      start();
    }
  };

  const tooltip = isProcessing
    ? "Transcribing…"
    : isRecording
      ? "Stop recording"
      : "Voice input";

  return (
    <Box
      className={className}
      sx={{ display: "flex", alignItems: "center", gap: 1 }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={selectedLang}
        onChange={(_, value) => {
          if (value) setSelectedLang(value as VoiceLanguage);
        }}
        disabled={isRecording || isProcessing}
      >
        {LANGUAGES.map((lang) => (
          <ToggleButton
            key={lang.code}
            value={lang.code}
            title={lang.label}
            sx={{ px: 1, textTransform: "none" }}
          >
            <span>{lang.flag}</span>
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" }, ml: 0.5 }}
            >
              {lang.label}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Tooltip title={tooltip}>
          <span>
            <IconButton
              onClick={handleClick}
              disabled={isProcessing}
              color={isRecording ? "error" : "default"}
              aria-label={tooltip}
              sx={{
                position: "relative",
                ...(isRecording && {
                  animation: "voicePulse 1.5s ease-out infinite",
                  "@keyframes voicePulse": {
                    "0%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.5)" },
                    "70%": { boxShadow: "0 0 0 10px rgba(211, 47, 47, 0)" },
                    "100%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)" },
                  },
                }),
              }}
            >
              {isProcessing ? (
                <CircularProgress size={24} />
              ) : (
                <MicIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        {error && (
          <Typography variant="caption" color="error" sx={{ maxWidth: 140 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
